import {
  AfterContentInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gl-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    standalone: false
})
export class CheckboxComponent
  implements OnInit, ControlValueAccessor, OnDestroy, OnChanges, AfterContentInit
{
  public startingValue: any;
  @Input() public name: string;
  @Input() public defaultChecked: boolean | string;
  @HostBinding('class.disabled') public isDisabled: boolean;
  @Input() public labelOnLeftSide: boolean;
  @ViewChild('inputCheck', { static: true, read: ElementRef }) public input: ElementRef;
  public onTouchedCallback: () => undefined;
  private subscription: Subscription;

  constructor(
    @Optional()
    @Self()
    public controlDir: NgControl,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    @Optional() public parentFormGroup: FormGroupDirective,
  ) {
    if (controlDir) {
      controlDir.valueAccessor = this;
    }
  }

  @HostBinding('class.checked') get checked() {
    return this.input.nativeElement.checked as boolean;
  }

  @HostListener('click', ['$event'])
  public onclick(event: MouseEvent) {
    if (this.isDisabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.setCheckedForInput();
    this.propagateChange(this.checked);
  }

  public ngOnInit(): void {
    if (this.defaultChecked) {
      this.changeCheckedForInput(this.defaultChecked as boolean);
    }
    if (!this.parentFormGroup) {
      return;
    }
  }

  public ngAfterContentInit(): void {
    this.isDisabled = this.elementRef.nativeElement.disabled as boolean;

    if (this.controlDir) {
      this.controlDir.valueChanges.subscribe((value) => {
        if (value !== this.checked) {
          this.changeCheckedForInput(value);
        }
      });
    }
    if (this.defaultChecked) {
      this.changeCheckedForInput(this.defaultChecked as boolean);
    } else if (this.startingValue !== undefined) {
      this.changeCheckedForInput(!!this.startingValue);
    }
  }

  public propagateChange = (_: any) => undefined;

  public writeValue(value: boolean): void {
    this.startingValue = value;
    if (this.input?.nativeElement) {
      this.changeCheckedForInput(!!value);
    }
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  private setCheckedForInput() {
    this.renderer.setProperty(this.input.nativeElement, 'checked', !this.checked);
  }

  private changeCheckedForInput(value: boolean) {
    this.renderer.setProperty(this.input.nativeElement, 'checked', value);
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultChecked) {
      this.changeCheckedForInput(changes.defaultChecked.currentValue);
    }
  }
}
